import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import {
  AsyncCreatableSelect,
  Button,
  FormActions,
  FormLayout,
  TextArea,
  TextInput,
} from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { FormRow as Row } from '../../../components/forms/FormRow';
import { FormSection as Section } from '../../../components/forms/FormSection';
import { loadSpecialtyOptions } from './api';
import { privilegesMachine } from './privileges.machine';
import { privilegesSchema } from './schemas';
import { PrivilegesFormValues } from './types';
import { errorHandler, isCustomPrivilegeForm } from './utils';

const TAGS = CT.privileges.form;

interface FormProps {
  actor: ActorRefFrom<typeof privilegesMachine>;
  handleClose: () => void;
}

export const PrivilegesForm = (props: FormProps) => {
  const { actor, handleClose } = props;
  const { crudRef } = useSelector(actor, (state) => state.context);
  const { form } = useSelector(crudRef, (state) => state.context);
  const { edit, create } = useSelector(crudRef, (state) => state.context.modal);
  const methods = useCustomForm<PrivilegesFormValues>({
    resolver: zodResolver(privilegesSchema),
    defaultValues: form,
  });

  const editPrivilege = (values: PrivilegesFormValues) =>
    crudRef.send({ type: 'ON_UPDATE', values: values });

  const createPrivilege = (values: PrivilegesFormValues) =>
    crudRef.send({ type: 'ON_SUBMIT', values: values });

  const deletePrivilege = () => {
    const id = methods.getValues('id');
    if (id) {
      handleClose();
      actor.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } });
    }
  };

  const onSubmitError = (err: unknown) =>
    errorHandler(`There was an error submitting the Privileges form`, err);

  const onSubmitSuccess = (values: PrivilegesFormValues) => {
    if (edit) {
      editPrivilege(values);
    } else {
      createPrivilege(values);
      actor.send({ type: 'ON_CLOSE_MODAL' });
    }
  };
  const { handleSubmit } = methods;
  const submitForm = handleSubmit(onSubmitSuccess, onSubmitError);
  const providerCategoryId = actor.getSnapshot().context.providerCategoryId;
  const isCustomPrivilege = isCustomPrivilegeForm(form);
  const fieldsDisabled = edit && !isCustomPrivilege;

  return (
    <FormProvider {...methods}>
      <form
        data-cy={TAGS.form}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        id="privileges-form"
      >
        <FormLayout className="bg-white">
          <Section componentsPerLine={2}>
            <Row>
              <AsyncCreatableSelect
                name={'specialtyName'}
                label={'Specialty'}
                disabled={fieldsDisabled}
                cyTag={TAGS.specialtyNameField}
                required
                minChars={false}
                loadOptions={(inputValue: string) =>
                  loadSpecialtyOptions(providerCategoryId, inputValue)
                }
                noOptionsMessage="Type to create a custom specialty"
                placeholder="Search or create a specialty..."
              />
            </Row>
            <Row>
              <TextInput
                name={'category'}
                label={'Category'}
                disabled={fieldsDisabled}
                required
                cyTag={TAGS.privilegeCategoryField}
              />
            </Row>
          </Section>
          <Section componentsPerLine={2}>
            <Row>
              <TextInput
                name={'privilegeType'}
                label={'Privilege Type'}
                required
                disabled={fieldsDisabled}
                cyTag={TAGS.privilegeTypeField}
              />
            </Row>
            <Row>
              <TextInput
                name={'assignmentDate'}
                label={'Assignment Date'}
                type="date"
                cyTag={TAGS.assignmentDateField}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextInput
                name={'privilegeName'}
                label={'Privilege Name'}
                required
                disabled={fieldsDisabled}
                cyTag={TAGS.privilegeNameField}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextArea name={'privilegeNotes'} label={'Notes'} cyTag={TAGS.privilegeNotesInput} />
            </Row>
          </Section>
          <FormActions>
            <Button
              type="button"
              cyTag={TAGS.cancelBtn}
              label={'Cancel'}
              onClick={handleClose}
              colorVariant="secondary"
            />
            {create ? (
              <Button type="button" cyTag={TAGS.submitBtn} label={'Save'} onClick={submitForm} />
            ) : (
              <>
                <Button
                  type="button"
                  cyTag={TAGS.removeBtn}
                  label={'Remove Privilege'}
                  onClick={deletePrivilege}
                  colorVariant="error"
                />
                <Button type="button" cyTag={TAGS.submitBtn} label={'Save'} onClick={submitForm} />
              </>
            )}
          </FormActions>
        </FormLayout>
      </form>
    </FormProvider>
  );
};
